import React, { useState } from 'react';
import { SectionHeader } from '../components/ui/SectionHeader';
import { Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { SEO } from '../components/SEO';
import { sendForm } from '../services/formService';

export const Contact: React.FC = () => {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'Booking Inquiry',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    
    const { success, error } = await sendForm('contact', formData);

    if (success) {
      setStatus('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      alert("Error submitting form: " + error);
      setStatus('idle');
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center container mx-auto px-6 py-12">
        <SEO title="Message Sent" description="Your message has been sent to AstralVibe." />
        <div className="bg-white/10 border border-astral-purple/30 p-8 md:p-12 text-center max-w-2xl w-full relative overflow-hidden animate-in fade-in zoom-in duration-500">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-astral-purple opacity-20 blur-[50px]"></div>
          
          <div className="mb-6 flex justify-center">
             <div className="w-20 h-20 rounded-full bg-astral-purple/20 flex items-center justify-center border border-astral-purple/50">
                <CheckCircle className="w-10 h-10 text-astral-purple" />
             </div>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-display font-bold uppercase text-white mb-4">Transmission Sent</h2>
          <p className="text-slate-200 text-base md:text-lg mb-8 leading-relaxed">
            Thank you for your message. It has been successfully received by our team.
            We'll get back to you as soon as possible.
          </p>
          
          <button 
            onClick={() => {
              setStatus('idle');
              setFormData({ name: '', email: '', subject: 'Booking Inquiry', message: '' });
            }}
            className="px-8 py-3 bg-white text-black font-bold uppercase tracking-widest text-xs hover:bg-astral-purple hover:text-white transition-colors"
          >
            Send Another
          </button>
        </div>
      </div>
    );
  }

  const contactStructuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
        "@type": "Organization",
        "name": "AstralVibe",
        "email": "booking@astralvibe.art",
        "telephone": "+39-379-332-2871",
        "url": "https://astralvibe.art",
        "contactPoint": [
            {
                "@type": "ContactPoint",
                "telephone": "+39-379-332-2871",
                "contactType": "booking",
                "areaServed": "Worldwide",
                "availableLanguage": ["English", "Italian"]
            }
        ]
    }
  };

  return (
    <div className="py-12">
      <SEO 
        title="Contact" 
        description="Get in touch with AstralVibe for bookings, demos, and press inquiries." 
        url="/contact"
        keywords={["contact astralvibe", "booking inquiry", "demo submission", "music agency contact"]}
        structuredData={contactStructuredData}
      />
      <div className="container mx-auto px-6">
        <SectionHeader subtitle="Connect" title="Start Transmission" />
        
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <p className="text-xl md:text-2xl text-white font-light mb-8">
                Ready to elevate your sound? <br />
                <span className="text-slate-300">Reach out for bookings, demos, or creative services.</span>
            </p>

            <div className="space-y-8">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-astral-purple flex-shrink-0">
                        <Mail />
                    </div>
                    <div>
                        <h5 className="text-white font-bold uppercase tracking-widest mb-1">Management & Booking</h5>
                        <p className="text-slate-300 text-sm mb-1 text-white break-all">booking@astralvibe.art</p>
                        <p className="text-slate-300 text-sm mb-1 text-white break-all">+39 3793322871</p>
                        <p className="text-slate-500 text-xs">For all artist bookings and event inquiries.</p>
                    </div>
                </div>
                
                 <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-astral-purple flex-shrink-0">
                        <Mail />
                    </div>
                    <div>
                        <h5 className="text-white font-bold uppercase tracking-widest mb-1">Demo Submission</h5>
                        <p className="text-slate-300 text-sm mb-1 break-all">info@astralvibe.art</p>
                        <p className="text-slate-500 text-xs">SoundCloud private links only.</p>
                    </div>
                </div>

                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-astral-purple flex-shrink-0">
                        <MapPin />
                    </div>
                    <div>
                        <h5 className="text-white font-bold uppercase tracking-widest mb-1">Headquarters</h5>
                        <p className="text-slate-300 text-sm">Via Squarciabocconi 19/c,</p>
                        <p className="text-slate-300 text-sm">Pescia, 51017, Italy</p>
                    </div>
                </div>

                <div className="relative overflow-hidden rounded-md border border-white/10 shadow-lg">
                    <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2884.66782163983!2d10.68686037666991!3d43.90158897109287!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132a7a7e8e6b12a3%3A0x8e83b27b4b1a62d!2sVia%20Squarciabocconi%2C%2C%2019c%2C%2051017%20Pescia%20PT%2C%20Italy!5e0!3m2!1sen!2sus!4v1720543666012!5m2!1sen!2sus"
                        className="w-full h-64 filter grayscale invert contrast-125 transition-all duration-500 hover:grayscale-0 hover:invert-0"
                        allowFullScreen={true}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="AstralVibe Headquarters Location"
                    ></iframe>
                </div>
            </div>
          </div>

          <div className="bg-white/10 border border-white/10 p-8 md:p-12 relative">
             <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-astral-purple to-transparent opacity-50"></div>
            
            <form name="contact" onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Name</label>
                        <input 
                          required 
                          type="text" 
                          name="name"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full bg-black border border-white/20 p-3 text-white focus:border-astral-purple focus:outline-none transition-colors" 
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Email</label>
                        <input 
                          required 
                          type="email" 
                          name="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="w-full bg-black border border-white/20 p-3 text-white focus:border-astral-purple focus:outline-none transition-colors" 
                        />
                    </div>
                </div>
                
                <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Subject</label>
                    <select 
                      name="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      className="w-full bg-black border border-white/20 p-3 text-white focus:border-astral-purple focus:outline-none transition-colors"
                    >
                        <option>Booking Inquiry</option>
                        <option>Creative Services</option>
                        <option>Press / Media</option>
                        <option>Other</option>
                    </select>
                </div>

                <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Message</label>
                    <textarea 
                      required 
                      rows={6} 
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full bg-black border border-white/20 p-3 text-white focus:border-astral-purple focus:outline-none transition-colors"
                    ></textarea>
                </div>

                <button 
                  type="submit" 
                  disabled={status === 'submitting'}
                  className="w-full bg-white text-black font-bold uppercase tracking-widest py-4 hover:bg-astral-purple hover:text-white transition-all flex items-center justify-center gap-2 group disabled:opacity-50"
                >
                    {status === 'submitting' ? 'Transmitting...' : 'Send Transmission'} 
                    {status !== 'submitting' && <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
                </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};